import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonContent,
  IonHeader,
  IonList,
  IonLoading,
  IonModal,
  IonSearchbar,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonAlert,
  useIonRouter,
} from "@ionic/react";
import React, { useContext, useRef, useState, ComponentType } from "react";
import CardExpandableHeader from "./CardExpandableHeader";
import BarcodeScannerButton from "./BarcodeScannerButton";
import StoresPopover from "./StoresPopover";
import SearchFooter from "./SearchFooter";
import QueryFilter from "./QueryFilter";
import {
  TranslationsContext,
  TranslationMessagesContext,
} from "../util/Translations";
import { StoresProps } from "../models/stores/Stores";
import {
  CriteriaParameter,
  FilterParam,
  maxResults,
  useQueryExec,
} from "../hooks/useQueryExec";
import { ModuleNames } from "../util/Constants";

interface StoresQueryCardProps extends StoresProps {
  module: string;
  querySection: string;
  placeholder: string;
  advancedSearchLabel: string;
  cardTitle: string;
  popoverSearch: (value: string) => Promise<any[]>;
  onBarcodeScan: (
    text?: string,
    storesTab?: string,
    router?: any,
    presentAlert?: any
  ) => void;
  ResultsComponent: ComponentType<any>;
  cardLink?: (storesTab: string) => string;
}

const StoresQueryCard: React.FC<StoresQueryCardProps> = ({
  expanded,
  setExpanded,
  storesTab,
  module,
  querySection,
  placeholder,
  advancedSearchLabel,
  cardTitle,
  popoverSearch,
  onBarcodeScan,
  ResultsComponent,
  cardLink,
}) => {
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const popover = useRef<HTMLIonPopoverElement>(null);
  const router = useIonRouter();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [presentAlert] = useIonAlert();
  const [isOpen, setIsOpen] = useState(false);
  const [searchYn, setSearchYn] = useState(false);
  const [criteriaParams, setCriteriaParams] = useState<CriteriaParameter[]>();
  const [queryName, setQueryName] = useState("");
  const {
    getQueryList,
    queryList,
    getFilterParams,
    filterParams,
    setParameter,
    queryRows,
    setQueryRows,
    executeSearch,
    getPriorityCodes,
    getStatusCodes,
    errorState,
    totalCount,
    queryCriteria,
    getQueryCriteria,
    getNoOfFieldsToDisplay,
    showLoading,
    setShowLoading,
    showToast,
    setShowToast,
    execFilter,
    filterRows,
    resultsFilter,
  } = useQueryExec(
    module,
    module,
    module === ModuleNames.WorkOrder ? "cf_work_order" : "requisition",
    undefined,
    module === ModuleNames.Requisition
  );

  const handleExpand = (expanded: boolean) => setExpanded(expanded);

  const handleSearch = (e: CustomEvent) => {
    let newVal = e.detail.value;
    setSearchValue(newVal);
    if (newVal.length >= 2) {
      popoverSearch(newVal).then((res) => {
        if (res.length > 0) {
          popover.current!.event = e;
          setPopoverOpen(true);
        } else if (popoverOpen) {
          setPopoverOpen(false);
        }
        setSearchResults(res);
      });
    } else {
      if (popoverOpen) setPopoverOpen(false);
      if (searchResults.length > 0) setSearchResults([]);
    }
  };

  const handleBarcodeScan = (text?: string) => {
    onBarcodeScan(text, storesTab, router, presentAlert);
  };

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const confirmSearch = async (
    currentQuery: string,
    filterParams?: FilterParam[],
    parameters?: CriteriaParameter[]
  ) => {
    setQueryRows([]);
    setCriteriaParams(parameters);
    setQueryName(currentQuery);
    setSearchYn(true);
    handleOpen();
  };

  const ResultsModal = (
    <IonModal isOpen={isOpen} onIonModalWillDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={handleClose}>
              {translations["lbl_btn_cancel"] || "Cancel"}
            </IonButton>
          </IonButtons>
          <IonTitle>{cardTitle}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <QueryFilter
          setShowLoading={setShowLoading}
          getQueryList={getQueryList}
          queryList={queryList}
          querySection={querySection}
          getFilterParams={getFilterParams}
          filterParams={filterParams}
          setParameter={setParameter}
          queryCriteria={queryCriteria}
          getQueryCriteria={getQueryCriteria}
          searchCallback={confirmSearch}
        />
        <ResultsComponent
          searchYn={searchYn}
          setSearchYn={setSearchYn}
          queryName={queryName}
          queryParams={filterParams}
          criteria={criteriaParams}
          queryRows={queryRows}
          filterParams={filterParams}
          setParameter={setParameter}
          executeSearch={executeSearch}
          getPriorityCodes={getPriorityCodes}
          getStatusCodes={getStatusCodes}
          getNoOfFieldsToDisplay={getNoOfFieldsToDisplay}
          errorState={errorState}
          totalCount={totalCount}
          showLoading={showLoading}
          setShowLoading={setShowLoading}
          onQueryRowClick={handleClose}
          cardClick={handleClose}
          cardLink={cardLink ? cardLink(storesTab) : undefined}
          filterRows={filterRows}
          filterMethod={execFilter}
          filterString={resultsFilter}
          storesRoute={true}
          storesTab={storesTab}
        />
      </IonContent>
      <SearchFooter totalRows={totalCount} filterRows={filterRows} />
    </IonModal>
  );

  return (
    <>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />
      {ResultsModal}
      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={
          translatedMessages["DisplayFirst"]?.MessageText.replace(
            "@1",
            maxResults
          )
            .replace("@2", cardTitle)
            .replace("@3", totalCount) ||
          `Displaying the first ${maxResults} ${cardTitle.toLowerCase()} of ${totalCount}.`
        }
        duration={2000}
        position="top"
      />
      <IonCard>
        <CardExpandableHeader
          expanded={expanded}
          onExpandClick={handleExpand}
          title={cardTitle}
        />
        <IonCardContent hidden={!expanded} style={{ paddingTop: 0 }}>
          <IonList>
            <IonSearchbar
              placeholder={placeholder}
              showClearButton="focus"
              inputMode="text"
              type="text"
              id="req-searchbar"
              value={searchValue}
              onIonChange={handleSearch}
              debounce={500}
            ></IonSearchbar>
            <IonButton color="primary" expand="full" onClick={handleOpen}>
              {advancedSearchLabel}
            </IonButton>
            <BarcodeScannerButton
              textButton
              onScan={handleBarcodeScan}
              expand="block"
            />
          </IonList>
        </IonCardContent>
      </IonCard>
      <StoresPopover
        popover={popover}
        popoverOpen={popoverOpen}
        setPopoverOpen={setPopoverOpen}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        querySection={querySection}
        storesTab={storesTab}
      />
    </>
  );
};

export default StoresQueryCard;
